import { BusinessApi } from '../api/business.api';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from './store';
import { startOfMonth } from 'date-fns';

import type { 
  IBusiness, 
  BusinessProduct, 
  IBusinessUser, 
  IPage, 
  ISalesReport, 
  ITransaction, 
  IBalance, 
  BusinessSales 
} from '@recargas-dominicanas/core/types';

type Sales = BusinessSales & { 
  loading: boolean; 
  failed: boolean; 
};

type BusinessState = {
  loading: boolean;
  failed: boolean;
  business: IBusiness | null;
  products: BusinessProduct[];
  users: IBusinessUser[];
  balances: IPage<IBalance> | null;
  transactions: IPage<ITransaction> | null;
  salesReports: IPage<ISalesReport> | null;
  sales: Sales;
};

export const businessSlice = createSlice({
  name: 'businessDetail',
  initialState: {
    loading: true,
    failed: false,
    business: null,
    products: [],
    users: [],
    balances: null,
    transactions: null,
    salesReports: null,
    sales: {
      loading: false,
      failed: false,
      balance: 0,
      sales: 0,
      profit: 0,
      count: 0,
      transactions: [],
      transactionsByProduct: []
    }
  } as BusinessState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setFailed(state, action: PayloadAction<boolean>) {
      state.failed = action.payload;
    },
    setBusiness(state, action: PayloadAction<IBusiness>) {
      state.business = action.payload;
    },
    setProducts(state, action: PayloadAction<BusinessProduct[]>) {
      state.products = action.payload;
    },
    setUsers(state, action: PayloadAction<IBusinessUser[]>) {
      state.users = action.payload;
    },
    setTransactions(state, action: PayloadAction<IPage<ITransaction>>) {
      state.transactions = action.payload;
    },
    setSalesReports(state, action: PayloadAction<IPage<ISalesReport>>) {
      state.salesReports = action.payload;
    },
    setBalances(state, action: PayloadAction<IPage<IBalance>>) {
      state.balances = action.payload;
    },
    setSales(state, action: PayloadAction<Partial<Sales>>) {
      state.sales = {
        ...state.sales,
        ...action.payload
      };
    },
    reset() {
      return {
        loading: true,
        failed: false,
        business: null,
        products: [],
        users: [],
        transactions: null,
        salesReports: null,
        balances: null,
        sales: {
          loading: false,
          failed: false,
          balance: 0,
          sales: 0,
          profit: 0,
          count: 0,
          transactions: [],
          transactionsByProduct: []
        }
      };
    }
  }
});

export function useBusinessStore() {

  const dispatch = useAppDispatch();
  const state = useAppSelector(state => state.business);

  async function fetchById(businessId: number) {

    try {

      dispatch(businessSlice.actions.setLoading(true));
      dispatch(businessSlice.actions.setFailed(false));

      const business = await BusinessApi.getById(businessId);

      dispatch(businessSlice.actions.setBusiness(business));
      dispatch(businessSlice.actions.setLoading(false));

    } catch (err) {

      console.error(err);
      dispatch(businessSlice.actions.setLoading(false));
      dispatch(businessSlice.actions.setFailed(true));
    }
  }

  async function fetchProducts(businessId: number) {
    try {
      const products = await BusinessApi.getProducts(businessId);
      dispatch(businessSlice.actions.setProducts(products));
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchUsers(businessId: number) {
    try {
      const users = await BusinessApi.getUsers(businessId);
      dispatch(businessSlice.actions.setUsers(users));
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchTransactions(businessId: number, page: number, size: number) {
    try {
      const transactions = await BusinessApi.getTransactions(businessId, page, size);
      dispatch(businessSlice.actions.setTransactions(transactions));
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchSalesReports(businessId: number, page: number, size: number) {
    try {
      const salesReports = await BusinessApi.getSalesReports(businessId, page, size);
      dispatch(businessSlice.actions.setSalesReports(salesReports));
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchBalances(businessId: number, page: number, size: number) {
    try {
      const balances = await BusinessApi.getBalances(businessId, page, size);
      dispatch(businessSlice.actions.setBalances(balances));
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchSales(
    businessId: number, 
    start = startOfMonth(new Date()), 
    end = new Date(), 
    period: 'day' | 'month' = 'day'
  ) {
  
    try {

      dispatch(businessSlice.actions.setSales({ loading: true, failed: false }));

      const salesData = await BusinessApi.getSales(businessId, {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        period
      });

      dispatch(businessSlice.actions.setSales({ loading: false, ...salesData }));

    } catch (err) {

      console.error(err);
      dispatch(businessSlice.actions.setSales({ loading: false, failed: true }));
    }
  }

  function reset() {
    dispatch(businessSlice.actions.reset());
  }

  return {
    ...state,
    fetchById,
    fetchProducts,
    fetchUsers,
    fetchTransactions,
    fetchSalesReports,
    fetchBalances,
    fetchSales,
    reset
  };
}
